import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { localDateString } from '@cerebro-diem/core';
import type { Task } from '@cerebro-diem/core';
import TaskItem from '../../components/TaskItem';
import { useContainers } from '../../services/containers';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { supabase } from '../../services/supabase';
import { tapLink } from '../../components/styles';
import ReminderDeliveryNote from './ReminderDeliveryNote';

/** One task, as a recent capture opens it: see it, edit or move it, set its reminder, and delete it. */
export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const containersQuery = useContainers();
  const taskQuery = useQuery({
    queryKey: queryKeys.task(id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error } = await supabase.from('tasks').select('*').eq('id', id as string).maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('task not found; it may have been deleted');
      return data as Task;
    },
  });

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Link to="/" className={`${tapLink} text-sm text-primary hover:underline`}>
        Back to Today
      </Link>
      <h1 className="text-2xl font-bold">Task</h1>
      {taskQuery.isError && <p className="text-sm text-error">Could not refresh the task: {errorMessage(taskQuery.error)}</p>}
      {containersQuery.isError && <p className="text-sm text-error">Could not refresh your projects and areas: {errorMessage(containersQuery.error)}</p>}
      {!taskQuery.data || !containersQuery.data ? (
        taskQuery.isError || containersQuery.isError ? null :
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : (
        <>
          <TaskItem task={taskQuery.data} today={localDateString()} containers={containersQuery.data} onDeleted={() => navigate('/')} />
          <ReminderDeliveryNote task={taskQuery.data} />
        </>
      )}
    </div>
  );
}
